/**
 * LandingHeader - Header for landing pages (Privat/Bedrift)
 * Støtter segmentering mellom privat og bedrift
 * @version 1.1.0 - B2B Auth routing
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CONFIG } from '../../../modules/landing';

export function LandingHeader({ show = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active segment based on current path
  const isBusinessPage = location.pathname.startsWith('/bedrift');
  const isPrivatePage = location.pathname === '/' || location.pathname === '';
  
  // Determine auth route based on current page
  const authRoute = isBusinessPage ? '/bedrift/auth' : '/auth';
  
  return (
    <header className="flex justify-between items-center py-6">
      {/* Logo & Brand */}
      <div 
        className={`
          flex items-center gap-5 transition-all duration-500 
          hover:opacity-90 cursor-pointer
          ${show ? 'opacity-100' : 'opacity-0'}
        `}
        onClick={() => navigate(isBusinessPage ? '/bedrift' : '/')}
        aria-label="Gå til forsiden"
      >
        <img 
          src={CONFIG.logo.src} 
          alt={CONFIG.logo.alt}
          className="w-16 h-16 rounded-xl object-contain shadow-lg shadow-[var(--copper)]/15"
        />
        <div>
          <span 
            className="block text-3xl font-bold text-[var(--text-main)] tracking-wide"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            RettVerk
          </span>
          <span className="block text-xs text-[var(--text-muted)] tracking-widest uppercase mt-0.5">
            {isBusinessPage ? 'Bedriftsportal' : 'Norsk juridisk assistent'}
          </span>
        </div>
      </div>
      
      {/* Navigation */}
      <nav 
        className={`
          flex items-center transition-all duration-500
          ${show ? 'opacity-100' : 'opacity-0'}
        `}
        style={{ transitionDelay: '100ms' }}
        aria-label="Hovednavigasjon"
      >
        {/* Segment Buttons: Privat & Bedrift */}
        <div className="flex items-center gap-4 mr-12">
          {/* Privat Button */}
          <button
            onClick={() => navigate('/')}
            aria-label="Gå til privat landingsside"
            aria-current={isPrivatePage ? 'page' : undefined}
            data-testid="nav-privat-btn"
            className={`
              px-4 py-2 text-sm font-medium rounded-lg
              transition-all duration-300
              ${isPrivatePage 
                ? 'bg-[var(--copper)]/10 text-[var(--copper)] border border-[var(--copper)]/30' 
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-highlight)]'
              }
            `}
          >
            Privat
          </button>
          
          {/* Bedrift Button */}
          <button
            onClick={() => navigate('/bedrift')}
            aria-label="Gå til bedriftslandingsside"
            aria-current={isBusinessPage ? 'page' : undefined}
            data-testid="nav-bedrift-btn"
            className={`
              px-4 py-2 text-sm font-medium rounded-lg
              transition-all duration-300
              ${isBusinessPage 
                ? 'bg-[var(--copper)]/10 text-[var(--copper)] border border-[var(--copper)]/30' 
                : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-highlight)]'
              }
            `}
          >
            Bedrift
          </button>
        </div>
        
        {/* Login Button - routes to B2B auth when on business page */}
        <button
          onClick={() => navigate(authRoute)}
          aria-label="Logg inn"
          data-testid="nav-login-btn"
          className="
            px-6 py-3 text-sm font-medium
            bg-[var(--bg-surface)] border border-[var(--border-subtle)]
            text-[var(--text-main)] rounded-lg
            hover:border-[var(--copper)]/50 hover:bg-[var(--bg-highlight)]
            transition-all duration-300
          "
        >
          Logg inn
        </button>
      </nav>
    </header>
  );
}

export default LandingHeader;
